import React, { useMemo } from 'react';
import { z } from 'zod';

import { Box } from '@mui/material';
import { useStrictVariablesRendering } from '@wp-block-builder/stores/WaypointDocumentStore/context';
import { useCurrentSkipLiquid, useCurrentVariables } from '@wp-block-builder/stores/WaypointRendererStore';

import { renderMarkdownString, renderOutputEscapedLiquidMarkdown } from '@waypoint/templates';

import defineBlock from './helpers/defineBlock';

export const TextBlockSchema = defineBlock(
  'Text',
  z.object({
    text: z.string().nullish(),
  })
);
export type TTextBlock = z.infer<typeof TextBlockSchema>;

function useRenderMarkdownString(template: string) {
  const variables = useCurrentVariables();
  const skipLiquid = useCurrentSkipLiquid();
  const strictVariablesRendering = useStrictVariablesRendering();

  return useMemo(() => {
    if (skipLiquid) {
      return renderMarkdownString(template);
    }
    return renderOutputEscapedLiquidMarkdown({
      template,
      variables,
      strictVariablesRendering,
    });
  }, [template, variables, skipLiquid, strictVariablesRendering]);
}

type TextBlockProps = {
  block: TTextBlock;
  placeholder?: JSX.Element;
};
export default function TextBlock({ block, placeholder }: TextBlockProps) {
  const { text } = block.props || {};
  const children = useRenderMarkdownString(text || '');
  return useMemo(() => {
    if (children.trim().length === 0) {
      return placeholder ?? null;
    }
    return (
      <Box
        sx={{
          '> div > *:first-child': { marginTop: 0 },
          '> div > *:last-child': { marginBottom: 0 },
          '> div > table': { my: 3 },
          '> div > table td, > table th': { py: 0.75, fontWeight: 'normal' },
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: children }} />
      </Box>
    );
  }, [children, placeholder]);
}
